/** Accuracy mode evidence panel parsing (Phase 3 Slice 2). */

import {
    getCitationDisplayTitle,
    isIrlCitation,
    isObservationCitation,
    linkCitationChunk,
} from './citationDisplay';

export const EVIDENCE_PANEL_TITLE = 'Evidence Used';
export const EVIDENCE_PANEL_SUBTITLE =
    'Documentary proof, management context, and pipeline filter decisions from this accuracy-mode answer.';
export const EVIDENCE_PANEL_EMPTY_MESSAGE =
    'No citable sources or retrieval debug breakdown is available for this response.';

export const EVIDENCE_EMPTY_PROOF =
    'No proof-tier VDR evidence was retained for this answer.';
export const EVIDENCE_EMPTY_CONTRADICTIONS = 'No conflicting evidence was identified.';
export const EVIDENCE_EMPTY_MANAGEMENT = 'No management responses were used.';
export const EVIDENCE_EMPTY_CONSULTANT = 'No consultant observations were used.';

const RETENTION_REASON_LABELS = {
    vdr_primary_proof: 'retained by evidence filter',
    irl_management_context: 'retained as management context',
    observation_context_only: 'retained as consultant context',
    contradiction_retained: 'retained as contradiction candidate',
};

export const EVIDENCE_SECTION_EMPTY_MESSAGES = {
    proof: 'No proof-tier VDR evidence was retained for this answer.',
    management: 'No management responses were used.',
    consultant: 'No consultant observations were used.',
    contradictions: 'No conflicting evidence was identified.',
};

export const SOURCE_BUCKET_LABELS = {
    proof: 'Proof',
    management: 'Management Context',
    consultant: 'Consultant Context',
};

export const FILTER_DECISION_LABELS = {
    include: 'Included',
    exclude: 'Excluded',
    context_only: 'Context only',
    contradiction_candidate: 'Contradiction',
};

function safeObject(value) {
    return value && typeof value === 'object' ? value : {};
}

function pickDebugBlocks(retrievalDebug) {
    const debug = safeObject(retrievalDebug);
    const coverage = safeObject(debug.coverage_report);
    return {
        debug,
        evidenceFilter: safeObject(debug.evidence_filter || coverage.evidence_filter),
        authorityRerank: safeObject(debug.authority_rerank || coverage.authority_rerank),
        multipass: safeObject(debug.accuracy_multipass || coverage.accuracy_multipass),
        decomposed: safeObject(debug.decomposed_confidence || coverage.decomposed_confidence),
    };
}

export function normalizeEvidenceSourceBucket(sourceType, citation, linkedChunk) {
    if (isObservationCitation(citation, linkedChunk)) return 'consultant';
    if (isIrlCitation(citation, linkedChunk)) return 'management';
    const st = String(sourceType || citation?.source_type || linkedChunk?.source_type || '')
        .toLowerCase()
        .trim();
    if (st === 'vdr_chunk' || st === 'vdr') return 'proof';
    if (st === 'irl' || st === 'irl_row') return 'management';
    if (st === 'observation') return 'consultant';
    return 'unknown';
}

export function formatFilterDecisionLabel(decision) {
    if (decision == null || decision === '') return null;
    const key = String(decision).toLowerCase().trim();
    return FILTER_DECISION_LABELS[key] || key.replace(/_/g, ' ');
}

export function formatExclusionReasonLabel(reason) {
    return String(reason || '')
        .trim()
        .replace(/_/g, ' ');
}

export function formatRetentionReasonSummary(reasons, decision) {
    const list = Array.isArray(reasons) ? reasons : [];
    if (list.length) {
        return list
            .map(r => RETENTION_REASON_LABELS[String(r).toLowerCase()] || formatExclusionReasonLabel(r))
            .join('; ');
    }
    const d = String(decision || '').toLowerCase();
    if (d === 'include') return 'retained by evidence filter';
    if (d === 'context_only') return 'retained as management context';
    if (d === 'contradiction_candidate') return 'retained as contradiction candidate';
    return null;
}

function formatAuthorityClassLabel(value) {
    if (value == null || value === '') return null;
    return formatExclusionReasonLabel(value);
}

export function formatReasonRetained(filterDecision) {
    if (!filterDecision || typeof filterDecision !== 'object') return null;
    return formatRetentionReasonSummary(filterDecision.reasons, filterDecision.decision);
}

function resolveFilename(citation, linkedChunk, evidence) {
    const meta = evidence?.metadata || linkedChunk?.metadata || {};
    return (
        citation?.document_name ||
        citation?.metadata_storage_name ||
        linkedChunk?.metadata_storage_name ||
        linkedChunk?.document_name ||
        meta.metadata_storage_name ||
        meta.document_name ||
        null
    );
}

function resolveAuthorityClass(filterDecision, authorityRerank, sourceId) {
    const ranked = Array.isArray(authorityRerank?.top_after) ? authorityRerank.top_after : [];
    const row = ranked.find(r => String(r?.source_id ?? r?.ref ?? '') === String(sourceId ?? ''));
    if (row?.authority_class) return formatAuthorityClassLabel(row.authority_class);
    if (filterDecision?.authority_class) return formatAuthorityClassLabel(filterDecision.authority_class);
    if (filterDecision?.evidence_role) return formatAuthorityClassLabel(filterDecision.evidence_role);
    return null;
}

export function summarizeExclusionReasons(exclusionReasons) {
    const raw = safeObject(exclusionReasons);
    return Object.entries(raw)
        .map(([reason, count]) => ({
            reason: formatExclusionReasonLabel(reason),
            count: Number(count) || 0,
        }))
        .filter(row => row.count > 0)
        .sort((a, b) => b.count - a.count);
}

function decisionIndex(decisions) {
    const byId = new Map();
    for (const d of Array.isArray(decisions) ? decisions : []) {
        if (!d || typeof d !== 'object') continue;
        const id = d.source_id != null ? String(d.source_id) : null;
        if (id) byId.set(id, d);
    }
    return byId;
}

function lookupFilterDecision(citation, linkedChunk, evidence, byId) {
    const ids = [
        citation?.source_id,
        linkedChunk?.source_id,
        evidence?.source_id,
        citation?.chunk_id,
        linkedChunk?.chunk_id,
    ]
        .filter(v => v != null && v !== '')
        .map(String);
    for (const id of ids) {
        const hit = byId.get(id);
        if (hit) return hit;
    }
    return null;
}

function authorityRankDetail(sourceId, authorityRerank) {
    const ranked = Array.isArray(authorityRerank?.top_after) ? authorityRerank.top_after : [];
    const row = ranked.find(
        r => String(r?.source_id ?? r?.ref ?? '') === String(sourceId ?? ''),
    );
    if (!row) return null;
    const parts = [];
    if (row.rank != null) parts.push(`Rank ${row.rank}`);
    if (row.composite_score != null) parts.push(`score ${row.composite_score}`);
    return parts.length ? parts.join(' · ') : null;
}

function buildPanelItem({
    citation,
    linkedChunk,
    evidence,
    filterDecision,
    authorityRerank,
    ctx,
}) {
    const bucket = normalizeEvidenceSourceBucket(
        citation?.source_type || evidence?.source_type,
        citation,
        linkedChunk,
    );
    const decision = filterDecision?.decision ?? null;
    const reasons = Array.isArray(filterDecision?.reasons) ? filterDecision.reasons : [];
    const sourceId = citation?.source_id ?? linkedChunk?.source_id ?? evidence?.source_id ?? null;
    const filename = resolveFilename(citation, linkedChunk, evidence);
    const displayTitle = getCitationDisplayTitle(citation, linkedChunk, ctx) || 'Source';
    const reasonRetained = formatReasonRetained(filterDecision);

    return {
        key: `${citation?.ref ?? sourceId ?? 'item'}`,
        ref: citation?.ref ?? null,
        citationLabel: citation?.ref != null ? `[${citation.ref}]` : null,
        title: displayTitle,
        filename: filename || (bucket === 'proof' ? displayTitle : null),
        bucket,
        sourceLabel: SOURCE_BUCKET_LABELS[bucket] || bucket,
        decision,
        decisionLabel: formatFilterDecisionLabel(decision),
        reasons,
        reasonRetained,
        authorityClass: resolveAuthorityClass(filterDecision, authorityRerank, sourceId),
        score:
            citation?.evidence_score ??
            filterDecision?.retrieval_score ??
            linkedChunk?.['@search.score'] ??
            null,
        authorityDetail: authorityRankDetail(sourceId, authorityRerank),
        retrievalPass: filterDecision?.retrieval_pass ?? null,
        evidenceRole: filterDecision?.evidence_role ?? null,
    };
}

export function extractContradictionPanelRows(retrievalDebug) {
    const { evidenceFilter, multipass } = pickDebugBlocks(retrievalDebug);
    const rows = [];
    const seen = new Set();

    for (const d of Array.isArray(evidenceFilter.decisions) ? evidenceFilter.decisions : []) {
        if (String(d?.decision).toLowerCase() !== 'contradiction_candidate') continue;
        const key = String(d.source_id || d.source_type || rows.length);
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({
            key,
            title: `${SOURCE_BUCKET_LABELS.proof} candidate`,
            sourceType: d.source_type,
            decisionLabel: formatFilterDecisionLabel(d.decision),
            reasons: Array.isArray(d.reasons) ? d.reasons : [],
            retrievalPass: d.retrieval_pass ?? null,
            score: d.retrieval_score ?? null,
        });
    }

    const multipassCount =
        multipass.contradiction_candidates_count ??
        evidenceFilter.contradiction_candidate_count;
    if (rows.length === 0 && Number(multipassCount) > 0) {
        rows.push({
            key: 'multipass-contradiction-count',
            title: 'Contradiction search',
            detail: `${multipassCount} candidate${Number(multipassCount) === 1 ? '' : 's'} from multi-pass retrieval`,
            reasons: [],
        });
    }

    return rows;
}

export function hasAccuracyEvidencePanelData(retrievalDebug, citations, evidence) {
    const cites = Array.isArray(citations) ? citations : [];
    const ev = Array.isArray(evidence) ? evidence : [];
    if (cites.length > 0 || ev.length > 0) return true;

    const { evidenceFilter, authorityRerank, multipass, decomposed } = pickDebugBlocks(retrievalDebug);

    if (evidenceFilter.active) {
        if (Array.isArray(evidenceFilter.decisions) && evidenceFilter.decisions.length > 0) return true;
        const counts = [
            evidenceFilter.included_count,
            evidenceFilter.excluded_count,
            evidenceFilter.context_only_count,
            evidenceFilter.contradiction_candidate_count,
        ];
        if (counts.some(n => Number(n) > 0)) return true;
        if (Object.keys(safeObject(evidenceFilter.exclusion_reasons)).length > 0) return true;
    }

    if (authorityRerank.active && Number(authorityRerank.ranked_count) > 0) return true;
    if (Array.isArray(authorityRerank.top_after) && authorityRerank.top_after.length > 0) return true;

    if (multipass.active) {
        if (Number(multipass.contradiction_candidates_count) > 0) return true;
        if (Array.isArray(multipass.passes_run) && multipass.passes_run.length > 0) return true;
    }

    if (decomposed.active) return true;

    return false;
}

export function isAccuracyEvidencePanelAvailable(accuracyMode, retrievalDebug, citations, evidence) {
    if (accuracyMode !== true) return false;
    return hasAccuracyEvidencePanelData(retrievalDebug, citations, evidence);
}

/**
 * Build panel sections for AccuracyEvidencePanel.
 */
export function buildAccuracyEvidencePanelModel({
    citations = [],
    evidence = [],
    retrievalDebug,
    chunks = [],
    ctx = {},
}) {
    const { evidenceFilter, authorityRerank } = pickDebugBlocks(retrievalDebug);
    const byDecision = decisionIndex(evidenceFilter.decisions);

    const proof = [];
    const management = [];
    const consultant = [];
    const seenRefs = new Set();

    const citeList = Array.isArray(citations) ? citations : [];
    const evidenceList = Array.isArray(evidence) ? evidence : [];

    for (const citation of citeList) {
        const linkedChunk = linkCitationChunk(citation, chunks);
        const refKey = String(citation?.ref ?? citation?.source_id ?? '');
        if (refKey && seenRefs.has(refKey)) continue;
        if (refKey) seenRefs.add(refKey);

        const matchedEvidence =
            evidenceList.find(
                ev =>
                    String(ev?.source_id) === String(citation?.source_id) ||
                    String(ev?.metadata?.chunk_id) === String(citation?.chunk_id),
            ) || null;

        const filterDecision = lookupFilterDecision(
            citation,
            linkedChunk,
            matchedEvidence,
            byDecision,
        );
        const decision = String(filterDecision?.decision || '').toLowerCase();

        if (decision === 'exclude') continue;
        if (decision === 'contradiction_candidate') continue;

        const item = buildPanelItem({
            citation,
            linkedChunk,
            evidence: matchedEvidence,
            filterDecision,
            authorityRerank,
            ctx,
        });

        if (item.bucket === 'proof') proof.push(item);
        else if (item.bucket === 'management') management.push(item);
        else if (item.bucket === 'consultant') consultant.push(item);
    }

    const proofSourceIds = new Set(
        proof
            .map(p => {
                const cite = citeList.find(c => String(c?.ref) === String(p?.ref));
                return cite?.source_id != null ? String(cite.source_id) : null;
            })
            .filter(Boolean),
    );

    // Proof-tier filter includes without a matching citation row
    for (const d of Array.isArray(evidenceFilter.decisions) ? evidenceFilter.decisions : []) {
        if (String(d?.decision).toLowerCase() !== 'include') continue;
        const st = String(d?.source_type || '').toLowerCase();
        if (st !== 'vdr_chunk' && st !== 'vdr') continue;
        const id = String(d.source_id || '');
        if (!id || proofSourceIds.has(id)) continue;
        proofSourceIds.add(id);
        proof.push({
            key: `filter-${id}`,
            ref: null,
            title: `Proof source ${id}`,
            filename: null,
            bucket: 'proof',
            sourceLabel: SOURCE_BUCKET_LABELS.proof,
            decision: d.decision,
            decisionLabel: formatFilterDecisionLabel(d.decision),
            reasons: Array.isArray(d.reasons) ? d.reasons : [],
            reasonRetained: formatReasonRetained(d),
            authorityClass: resolveAuthorityClass(d, authorityRerank, id),
            score: d.retrieval_score ?? null,
            authorityDetail: authorityRankDetail(id, authorityRerank),
            retrievalPass: d.retrieval_pass ?? null,
            evidenceRole: d.evidence_role ?? null,
        });
    }

    const contradictions = extractContradictionPanelRows(retrievalDebug);
    const excluded = summarizeExclusionReasons(evidenceFilter.exclusion_reasons);

    const sections = [
        {
            key: 'proof',
            title: 'Proof Evidence',
            items: proof,
            emptyMessage: EVIDENCE_SECTION_EMPTY_MESSAGES.proof,
        },
        {
            key: 'management',
            title: 'Management Context',
            items: management,
            emptyMessage: EVIDENCE_SECTION_EMPTY_MESSAGES.management,
        },
        {
            key: 'consultant',
            title: 'Consultant Context',
            items: consultant,
            emptyMessage: EVIDENCE_SECTION_EMPTY_MESSAGES.consultant,
        },
        {
            key: 'contradictions',
            title: 'Contradictions / Caveats',
            items: contradictions,
            emptyMessage: EVIDENCE_SECTION_EMPTY_MESSAGES.contradictions,
        },
        {
            key: 'excluded',
            title: 'Excluded / Downgraded',
            items: excluded.map((row, idx) => ({
                key: `ex-${row.reason}-${idx}`,
                title: row.reason,
                count: row.count,
            })),
            emptyMessage: null,
        },
    ];

    const hasSectionContent =
        sections.some(s => (s.items || []).length > 0) ||
        sections.some(s => s.emptyMessage && s.key !== 'excluded');

    return {
        title: EVIDENCE_PANEL_TITLE,
        subtitle: EVIDENCE_PANEL_SUBTITLE,
        emptyMessage: EVIDENCE_PANEL_EMPTY_MESSAGE,
        sections,
        hasSectionContent,
        proofSourcesPresent: evidenceFilter.proof_sources_present,
        filterActive: Boolean(evidenceFilter.active),
        rerankActive: Boolean(authorityRerank.active),
    };
}
